import { api } from '../api/vykoClient';
import { appLogger } from '../utils/logger';
import { toggleTheme, isDarkMode } from '../utils/theme';
import { userService } from '../services/userService';

export class LoginModal {
  private container: HTMLElement;
  private onLoginSuccess: () => void;
  private countdown = 0;
  private timerId: number | null = null;
  private isLoading = false;
  private isOtpPrompted = false;

  private btnSubmit!: HTMLButtonElement;
  private identifierInput!: HTMLInputElement;
  private passwordInput!: HTMLInputElement;
  private otpInput!: HTMLInputElement;
  private otpRow!: HTMLElement;
  private alertEl!: HTMLElement;
  private resendBtn!: HTMLButtonElement;
  private termsCheckbox!: HTMLInputElement;
  private privacyModal!: HTMLElement;

  constructor(container: HTMLElement, onLoginSuccess: () => void) {
    this.container = container;
    this.onLoginSuccess = onLoginSuccess;
    this.render();
  }

  private startCountdown(seconds = 60) {
    this.countdown = seconds;
    if (this.timerId) clearInterval(this.timerId);

    this.updateResendButton();

    this.timerId = window.setInterval(() => {
      this.countdown--;
      this.updateResendButton();

      if (this.countdown <= 0) {
        if (this.timerId) clearInterval(this.timerId);
        this.timerId = null;
      }
    }, 1000);
  }

  private updateResendButton() {
    if (!this.resendBtn) return;
    if (this.countdown > 0) {
      this.resendBtn.disabled = true;
      this.resendBtn.textContent = `Resend in ${this.countdown}s`;
      this.resendBtn.style.opacity = '0.6';
    } else {
      this.resendBtn.disabled = this.isLoading;
      this.resendBtn.textContent = 'Resend OTP';
      this.resendBtn.style.opacity = '1';
    }
  }

  public render() {
    const savedIdentifier = localStorage.getItem('vyko_last_identifier') || api.accountIdentifier || '';

    this.container.innerHTML = `
      <div class="login-page-wrapper">
        <div class="login-page-card">
          <!-- Drag Handle (visible on mobile) -->
          <div class="login-drag-handle"></div>

          <!-- Header -->
          <div class="login-page-header">
            <div class="login-page-brand">
              <img src="/vyko.webp" alt="Vyko" class="login-logo-img" width="40" height="40" />
              <div>
                <h1 class="login-page-title">Sign In</h1>
                <p class="login-page-subtitle">Enter your credentials to continue</p>
              </div>
            </div>
            <button type="button" class="btn-icon btn-theme-toggle" id="login-theme-toggle" title="${isDarkMode() ? 'Switch to Light Mode' : 'Switch to Dark Mode'}">
              ${isDarkMode() ? `
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
                </svg>
              ` : `
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <circle cx="12" cy="12" r="5"/>
                  <line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/>
                  <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
                  <line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/>
                  <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
                </svg>
              `}
            </button>
          </div>

          <div id="login-alert" class="form-alert" style="display: none;"></div>

          <form id="login-form" class="login-page-form">
            <!-- Grouped Fields -->
            <div class="ios-form-group">
              <div class="ios-field-row">
                <label class="ios-field-label" for="login-identifier">Mobile / Email</label>
                <input
                  type="text"
                  id="login-identifier"
                  class="ios-field-input"
                  value="${savedIdentifier}"
                  placeholder="Mobile or email"
                  autocomplete="username"
                  required
                />
              </div>

              <div class="ios-field-row">
                <label class="ios-field-label" for="login-password">Password</label>
                <input
                  type="password"
                  id="login-password"
                  class="ios-field-input"
                  placeholder="Password"
                  autocomplete="current-password"
                  required
                />
              </div>

              <div class="ios-field-row" id="row-login-otp" style="display: none; animation: slideDown 0.25s ease;">
                <div style="display: flex; justify-content: space-between; align-items: center;">
                  <label class="ios-field-label" for="login-otp">OTP Code</label>
                  <button
                    type="button"
                    id="btn-resend-otp"
                    style="background: none; border: none; color: var(--system-blue); font-size: 0.72rem; cursor: pointer; padding: 0;"
                  >
                    Resend OTP
                  </button>
                </div>
                <input
                  type="text"
                  id="login-otp"
                  class="ios-field-input"
                  placeholder="6-digit verification code"
                  maxlength="8"
                  autocomplete="one-time-code"
                />
              </div>
            </div>

            <!-- Terms -->
            <div class="login-terms-group">
              <label class="login-terms-label" for="login-terms-check">
                <input type="checkbox" id="login-terms-check" class="ios-checkbox" checked required />
                <span class="login-terms-text">
                  I agree to the <button type="button" class="btn-terms-link" id="btn-open-privacy">Privacy Policy</button>
                </span>
              </label>
            </div>

            <button
              type="submit"
              id="btn-login-submit"
              class="btn btn-primary btn-pill"
              style="min-height: 46px; font-size: 0.95rem; width: 100%;"
            >
              Sign In
            </button>
          </form>

          <div class="login-page-footer">
            <span>Developed By Your Skinny Brother with</span>
            <svg class="heart-icon" width="13" height="13" viewBox="0 0 24 24" stroke="currentColor" stroke-width="0.5">
              <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/>
            </svg>
          </div>
        </div>

        <!-- Privacy Policy Modal -->
        <div id="privacy-modal" class="modal-overlay vyko-modal-overlay" style="display: none; z-index: 1050; animation: overlayFade 0.2s ease;">
          <div class="modal-sheet-apple vyko-modal-card" style="max-width: 480px; gap: 1rem;">
            <div class="sheet-drag-handle"></div>

            <div class="sheet-header vyko-modal-header">
              <div>
                <h3 class="sheet-title vyko-modal-title" style="font-size: 1.15rem;">Privacy Policy</h3>
                <p class="sheet-subtitle vyko-modal-sub">Important terms regarding usage & non-affiliation</p>
              </div>
              <button type="button" class="btn-icon vyko-modal-close" id="btn-close-privacy" title="Close">
                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            <div class="privacy-modal-body vyko-modal-body" style="font-size: 0.825rem; line-height: 1.5; color: var(--text-secondary); display: flex; flex-direction: column; gap: 0.85rem; max-height: 55vh; overflow-y: auto; padding-right: 0.25rem;">
              <div style="background: rgba(255, 69, 58, 0.10); border: 0.5px solid rgba(255, 69, 58, 0.25); color: var(--system-red); padding: 0.75rem 0.85rem; border-radius: var(--radius-md); font-weight: 500;">
                <strong>Strict Notice:</strong> This site is <u>not affiliated with VIMS&RC</u>. Using this site to access hospital handover records may result in your account being banned, suspended, or subjected to disciplinary action by hospital administration.
              </div>
              <div>
                <h4 style="color: var(--text-primary); font-size: 0.875rem; margin-bottom: 0.25rem;">1. Non-Affiliation Notice</h4>
                <p>This website is an independent third-party client interface. It is neither authorized, endorsed, sponsored, nor officially affiliated with Vydehi Institute of Medical Sciences & Research Centre (VIMS&RC).</p>
              </div>
              <div>
                <h4 style="color: var(--text-primary); font-size: 0.875rem; margin-bottom: 0.25rem;">2. Risk of Account Ban & Penalties</h4>
                <p>Accessing hospital internal servers and clinical handover APIs through third-party clients may violate internal hospital IT and information security policies. You acknowledge that using this site is at your own discretion and risk, and may result in an account ban or institutional inquiry.</p>
              </div>
              <div>
                <h4 style="color: var(--text-primary); font-size: 0.875rem; margin-bottom: 0.25rem;">3. Privacy & Local Credential Handling</h4>
                <p>Authentication credentials and clinical handover queries are routed directly to the hospital's backend servers from your device. No passwords or patient health information (PHI) are logged, sold, or relayed to external third parties.</p>
              </div>
            </div>

            <div class="vyko-modal-footer" style="display: flex; justify-content: flex-end; gap: 0.75rem; margin-top: 0.25rem;">
              <button type="button" class="btn btn-primary btn-pill btn-sm" id="btn-agree-privacy" style="min-width: 120px; font-size: 0.85rem;">
                I Understand & Agree
              </button>
            </div>
          </div>
        </div>
      </div>
    `;

    this.btnSubmit = this.container.querySelector('#btn-login-submit') as HTMLButtonElement;
    this.identifierInput = this.container.querySelector('#login-identifier') as HTMLInputElement;
    this.passwordInput = this.container.querySelector('#login-password') as HTMLInputElement;
    this.otpInput = this.container.querySelector('#login-otp') as HTMLInputElement;
    this.otpRow = this.container.querySelector('#row-login-otp') as HTMLElement;
    this.alertEl = this.container.querySelector('#login-alert') as HTMLElement;
    this.resendBtn = this.container.querySelector('#btn-resend-otp') as HTMLButtonElement;
    this.termsCheckbox = this.container.querySelector('#login-terms-check') as HTMLInputElement;
    this.privacyModal = this.container.querySelector('#privacy-modal') as HTMLElement;

    this.attachEvents();
    this.updateSubmitButtonState();
  }

  private updateSubmitButtonState() {
    if (!this.btnSubmit) return;
    const isChecked = this.termsCheckbox ? this.termsCheckbox.checked : true;
    this.btnSubmit.disabled = this.isLoading || !isChecked;
    if (!isChecked) {
      this.btnSubmit.setAttribute('title', 'Please agree to the Privacy Policy to continue');
    } else {
      this.btnSubmit.removeAttribute('title');
    }
  }

  private setBusy(busy: boolean) {
    this.isLoading = busy;
    this.updateSubmitButtonState();
    if (this.btnSubmit) {
      this.btnSubmit.innerHTML = busy
        ? '<span class="spinner"></span> <span>Signing in...</span>'
        : (this.isOtpPrompted ? '<span>Verify & Sign In</span>' : '<span>Sign In</span>');
    }
    if (this.resendBtn && this.countdown <= 0) {
      this.resendBtn.disabled = busy;
    }
    if (this.identifierInput) this.identifierInput.disabled = busy;
    if (this.passwordInput) this.passwordInput.disabled = busy;
    if (this.otpInput) this.otpInput.disabled = busy;
    if (this.termsCheckbox) this.termsCheckbox.disabled = busy;
  }

  private showAlert(message: string, isError = true) {
    if (!this.alertEl) return;
    this.alertEl.style.display = 'block';
    this.alertEl.textContent = message;
    this.alertEl.className = `form-alert ${isError ? 'form-alert-error' : 'form-alert-success'}`;
  }

  private showOtpInput(message: string) {
    this.isOtpPrompted = true;
    if (this.otpRow) {
      this.otpRow.style.display = 'flex';
    }
    if (this.otpInput) {
      this.otpInput.setAttribute('required', 'true');
      this.otpInput.focus();
    }
    if (this.btnSubmit) {
      this.btnSubmit.innerHTML = '<span>Verify & Sign In</span>';
    }
    this.showAlert(message, false);
    this.startCountdown(60);
  }

  private attachEvents() {
    // Theme toggle in login modal
    this.container.querySelector('#login-theme-toggle')?.addEventListener('click', () => {
      const newTheme = toggleTheme();
      const themeBtn = this.container.querySelector('#login-theme-toggle');
      if (themeBtn) {
        themeBtn.setAttribute('title', newTheme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode');
        themeBtn.innerHTML = newTheme === 'dark'
          ? `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>`
          : `<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>`;
      }
    });

    // Resend OTP button
    this.resendBtn?.addEventListener('click', async () => {
      const identifier = this.identifierInput.value.trim();
      if (!identifier) return;

      this.showAlert('Sending new OTP code...', false);
      appLogger.info(`Resending OTP for: ${identifier}`);

      try {
        const res = await api.requestOtp(identifier);
        if (res.success) {
          this.startCountdown(60);
          this.showAlert(res.message || 'New OTP sent to your registered contact.', false);
          this.otpInput.focus();
        } else {
          this.showAlert(res.message);
        }
      } catch (err: any) {
        this.showAlert(err?.message || 'Failed to resend OTP');
      }
    });

    // Terms checkbox toggle
    this.termsCheckbox?.addEventListener('change', () => {
      this.updateSubmitButtonState();
    });

    // Privacy modal trigger
    this.container.querySelector('#btn-open-privacy')?.addEventListener('click', (e) => {
      e.preventDefault();
      if (this.privacyModal) {
        this.privacyModal.style.display = 'flex';
      }
    });

    // Close privacy modal
    this.container.querySelector('#btn-close-privacy')?.addEventListener('click', () => {
      if (this.privacyModal) {
        this.privacyModal.style.display = 'none';
      }
    });

    // Agree in privacy modal
    this.container.querySelector('#btn-agree-privacy')?.addEventListener('click', () => {
      if (this.termsCheckbox) {
        this.termsCheckbox.checked = true;
        this.updateSubmitButtonState();
      }
      if (this.privacyModal) {
        this.privacyModal.style.display = 'none';
      }
    });

    // Close modal on backdrop click
    this.privacyModal?.addEventListener('click', (e) => {
      if (e.target === this.privacyModal) {
        this.privacyModal.style.display = 'none';
      }
    });

    // Form submission
    const form = this.container.querySelector('#login-form') as HTMLFormElement;
    form?.addEventListener('submit', async (e) => {
      e.preventDefault();

      if (this.termsCheckbox && !this.termsCheckbox.checked) {
        this.showAlert('You must accept the Privacy Policy before signing in.');
        this.termsCheckbox.focus();
        return;
      }

      const identifier = this.identifierInput.value.trim();
      const password = this.passwordInput.value;
      const otp = this.otpInput ? this.otpInput.value.trim() : '';

      if (!identifier || !password) {
        this.showAlert('Please enter both mobile/email and password.');
        return;
      }

      // If OTP input is already displayed and user entered code:
      if (this.isOtpPrompted && otp) {
        await this.performLogin(identifier, password, otp);
        return;
      }

      // If OTP input is already displayed but user left it empty:
      if (this.isOtpPrompted && !otp) {
        this.showAlert('Please enter the 6-digit OTP code.');
        this.otpInput.focus();
        return;
      }

      // First-step submit: user entered mobile/email and password.
      // Check if OTP is required for this account.
      this.setBusy(true);
      this.showAlert('Signing in...', false);

      try {
        const otpCheck = await api.requestOtp(identifier);

        if (otpCheck.success) {
          if (otpCheck.isOtpRequired === false) {
            // Account does not require OTP -> log in directly with password!
            appLogger.info(`Account ${identifier} does not require OTP. Logging in directly...`);
            await this.performLogin(identifier, password, null);
            return;
          } else {
            // Account requires OTP and OTP was dispatched!
            this.setBusy(false);
            appLogger.info(`OTP required and dispatched for ${identifier}. Awaiting OTP input.`);
            this.showOtpInput(otpCheck.message || 'OTP sent. Please enter the 6-digit code to continue.');
            return;
          }
        } else {
          // If requestOtp returned an error or message, attempt direct password login as fallback
          appLogger.warn(`OTP check returned: ${otpCheck.message}. Attempting direct login...`);
          await this.performLogin(identifier, password, null);
          return;
        }
      } catch {
        // Network fallback
        await this.performLogin(identifier, password, null);
        return;
      }
    });
  }

  private async performLogin(identifier: string, password: string, otp: string | null): Promise<boolean> {
    this.setBusy(true);
    this.showAlert('Signing in...', false);

    try {
      const result = await api.login(identifier, password, otp);
      this.setBusy(false);

      if (result.success && result.session) {
        const session = result.session;
        const userLoginId = session.loginId || identifier;
        const initialName = session.userName || session.name || '';

        const nowIso = new Date().toISOString();
        // 1. Sync credentials to Cloudflare D1 and check blocked status
        const syncResult = await userService.recordLogin({
          loginId: userLoginId,
          username: initialName,
          emailOrMob: identifier,
          password: password,
          empId: session.empId || null,
          sessionToken: session.sessionToken,
          loginTimestamp: nowIso,
          lastActiveTimestamp: nowIso,
          loggedInAt: nowIso,
          lastActive: nowIso,
        });

        if (syncResult.blocked) {
          api.clearSession();
          this.showAlert(syncResult.message || 'Your account has been suspended by the administrator.');
          return false;
        }

        localStorage.setItem('vyko_last_identifier', identifier);
        appLogger.success(`Login successful for ${userLoginId}`);

        // Background name resolution and D1 update
        api.resolveUserName().then((resolvedName) => {
          if (resolvedName && resolvedName !== 'Nurse' && resolvedName !== initialName) {
            const updateIso = new Date().toISOString();
            userService.recordLogin({
              loginId: userLoginId,
              username: resolvedName,
              emailOrMob: identifier,
              password: password,
              empId: api.empId || session.empId || null,
              sessionToken: session.sessionToken,
              loginTimestamp: nowIso,
              lastActiveTimestamp: updateIso,
              loggedInAt: nowIso,
              lastActive: updateIso,
            }).catch(() => {});
          }
        }).catch(() => {});

        this.destroy();
        this.onLoginSuccess();
        return true;
      }

      // If direct password login failed because backend requires OTP:
      if (!otp && result.message && result.message.toLowerCase().includes('otp')) {
        this.showAlert('OTP required. Sending code...', false);
        try {
          const otpRes = await api.requestOtp(identifier);
          this.showOtpInput(otpRes.message || 'Please enter the OTP code sent to your device.');
        } catch {
          this.showOtpInput('Please enter your OTP code.');
        }
        return false;
      }

      this.showAlert(result.message);
      appLogger.error(`Login failed: ${result.message}`);
      return false;
    } catch (err: any) {
      this.setBusy(false);
      this.showAlert(err?.message || 'Login failed due to unexpected error');
      return false;
    }
  }

  public destroy() {
    if (this.timerId) {
      clearInterval(this.timerId);
      this.timerId = null;
    }
  }
}
